import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  Plus, Edit, Trash2, Search, Loader2, FileText, Eye,
  ArrowLeft, Save, Upload, X, MoreHorizontal, CheckCircle,
  EyeOff, Image as ImageIcon, Tag, AlertTriangle, RefreshCw,
  ThumbsDown, Globe, SendToBack, Flag,
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import SharingPanel from '../../components/shared/SharingPanel';
import TemplateStatsCard from '../../components/shared/TemplateStatsCard';
import AdminLayout from './AdminLayout';
import TemplateThumbnail from '../../components/shared/TemplateThumbnail';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TEMPLATE_TYPES = [
  'Flyer', 'Social Media', 'Poster', 'Presentation', 'Resume',
  'Business Card', 'Invitation', 'Banner', 'Brochure', 'Certificate', 'Other',
];

const STATUS_BADGE = {
  draft:     'bg-gray-100 text-gray-500',
  pending:   'bg-amber-100 text-amber-700',
  approved:  'bg-blue-100 text-blue-700',
  published: 'bg-emerald-100 text-emerald-700',
  rejected:  'bg-red-100 text-red-600',
};

// ─── Thumbnail cell ───────────────────────────────────────────────────────────
// Uses the shared TemplateThumbnail component — same renderer as the public
// templates page, editor, designer pages, etc. PageThumbnailPreview is the
// live layer; the stored t.thumbnail overlays it when it loads successfully.
const ThumbnailCell = ({ t }) => {
  if (t.template_json || t.thumbnail) {
    return <TemplateThumbnail template={t} />;
  }
  return (
    <div className="w-full h-full flex items-center justify-center">
      <ImageIcon className="w-4 h-4 text-gray-300" />
    </div>
  );
};

// ─── File Upload Helper ────────────────────────────────────────────────────────
const uploadFile = async (file, token) => {
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(`${API_URL}/api/admin/content/assets/upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  if (!res.ok) throw new Error('Upload failed');
  const asset = await res.json();
  return asset.url?.startsWith('http') ? asset.url : `${API_URL}${asset.url}`;
};

// ─── Upload Field ──────────────────────────────────────────────────────────────
const UploadField = ({ label, value, onChange, accept = 'image/*', hint }) => {
  const ref = useRef();
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const url = await uploadFile(file, token);
      onChange(url);
    } catch {
      toast({ title: 'Upload failed', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div
        className="border-2 border-dashed border-gray-200 rounded-xl p-4 text-center cursor-pointer hover:border-violet-300 hover:bg-violet-50/30 transition-colors"
        onClick={() => ref.current?.click()}
        onDragOver={e => e.preventDefault()}
        onDrop={e => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
      >
        {uploading ? (
          <div className="flex flex-col items-center gap-1 py-2">
            <Loader2 className="w-5 h-5 animate-spin text-violet-500" />
            <p className="text-xs text-gray-400">Uploading…</p>
          </div>
        ) : value ? (
          <div className="flex items-center gap-3">
            {accept.startsWith('image') ? (
              <img src={value} alt="" className="w-16 h-16 object-cover rounded-lg border" />
            ) : (
              <div className="w-16 h-16 bg-violet-100 rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6 text-violet-500" />
              </div>
            )}
            <div className="flex-1 text-left">
              <p className="text-xs text-gray-500 break-all line-clamp-2">{value}</p>
              <button
                className="text-xs text-violet-600 hover:underline mt-1"
                onClick={e => { e.stopPropagation(); onChange(''); }}
              >
                Remove
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1 py-2">
            <Upload className="w-5 h-5 text-gray-300" />
            <p className="text-xs text-gray-500 font-medium">Click to upload or drag & drop</p>
            {hint && <p className="text-[11px] text-gray-400">{hint}</p>}
          </div>
        )}
      </div>
      <input
        ref={ref}
        type="file"
        accept={accept}
        className="hidden"
        onChange={e => handleFile(e.target.files[0])}
      />
    </div>
  );
};

// ─── Preview Dialog ────────────────────────────────────────────────────────────
const PreviewDialog = ({ template, open, onClose }) => {
  // Single rendering pipeline: PageThumbnailPreview as the live base, stored
  // thumbnail overlaid on top via TemplateThumbnail — identical to public site.
  // Falls back to a pure live PageThumbnailPreview render when no stored image
  // exists but template_json is present.
  const firstPage = template?.template_json?.pages?.[0];
  const hasRender = !!firstPage || !!template?.thumbnail || !!template?.preview_url;

  // Lazy report-summary fetch when the preview opens (requirement 5).
  const navigate = useNavigate();
  const [reportSummary, setReportSummary] = useState(null);
  useEffect(() => {
    if (!open || !template?.id) { setReportSummary(null); return; }
    let active = true;
    const token = localStorage.getItem('admin_token');
    fetch(`${API_URL}/api/admin/marketplace/templates/${template.id}/report-summary`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (active) setReportSummary(d); })
      .catch(() => {});
    return () => { active = false; };
  }, [open, template?.id]);

  return (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className="sm:max-w-2xl">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Eye className="w-4 h-4 text-violet-500" />
          {template?.name}
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-4">
        {hasRender ? (
          <div className="w-full max-w-md mx-auto rounded-xl border bg-gray-50 overflow-hidden">
            {firstPage ? (
              <TemplateThumbnail template={template} />
            ) : (
              <img
                src={template?.preview_url || template?.thumbnail}
                alt={template?.name}
                className="w-full max-h-96 object-contain bg-gray-50"
              />
            )}
          </div>
        ) : (
          <div className="w-full h-64 bg-gray-100 rounded-xl flex flex-col items-center justify-center gap-2">
            <ImageIcon className="w-10 h-10 text-gray-300" />
            <p className="text-sm text-gray-400">No preview available</p>
          </div>
        )}
        <div className="grid grid-cols-3 gap-3 text-sm">
          <div className="bg-gray-50 rounded-lg px-3 py-2">
            <p className="text-xs text-gray-400 font-medium">Type</p>
            <p className="text-gray-800 font-semibold mt-0.5">{template?.template_type || '—'}</p>
          </div>
          <div className="bg-gray-50 rounded-lg px-3 py-2">
            <p className="text-xs text-gray-400 font-medium">Dimensions</p>
            <p className="text-gray-800 font-semibold mt-0.5">{template?.canvas_width}×{template?.canvas_height}px</p>
          </div>
          <div className="bg-gray-50 rounded-lg px-3 py-2">
            <p className="text-xs text-gray-400 font-medium">Access</p>
            <p className="text-gray-800 font-semibold mt-0.5">{template?.is_premium ? 'Premium' : 'Free'}</p>
          </div>
        </div>
        {template?.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {template.tags.map(tag => (
              <span key={tag} className="text-xs bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full">
                {tag}
              </span>
            ))}
          </div>
        )}
        {reportSummary && (
          <div className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
            <div className="flex items-center gap-2 text-sm">
              <Flag className={`w-4 h-4 ${reportSummary.open > 0 ? 'text-red-500' : 'text-gray-400'}`} />
              <span className="text-gray-700 font-medium">
                Reports: {reportSummary.open} open
                <span className="text-gray-400 font-normal"> / {reportSummary.total} total</span>
              </span>
            </div>
            <button
              onClick={() => navigate('/admin/marketplace/reports')}
              className="text-xs text-violet-600 hover:text-violet-700 font-medium"
            >
              View reports →
            </button>
          </div>
        )}
      </div>
    </DialogContent>
  </Dialog>
  );
};

// ─── Actions Dropdown ──────────────────────────────────────────────────────────
const ActionsMenu = ({ template, onEdit, onEditCanvas, onPreview, onApprove, onReject, onPublish, onUnpublish, onSubmit, onDelete }) => {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });
  const triggerRef = useRef();
  const dropdownRef = useRef();

  useEffect(() => {
    const handler = (e) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        triggerRef.current && !triggerRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleOpen = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    }
    setOpen(o => !o);
  };

  const status = template.status || 'draft';

  return (
    <div ref={triggerRef} className="inline-block">
      <Button
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0 text-gray-400 hover:text-gray-700"
        onClick={handleOpen}
      >
        <MoreHorizontal className="w-4 h-4" />
      </Button>
      {open && (
        <div
          ref={dropdownRef}
          style={{ position: 'fixed', top: menuPos.top, right: menuPos.right, zIndex: 9999 }}
          className="bg-white border border-gray-100 rounded-xl shadow-lg py-1 w-48"
        >
          <button
            className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-violet-700 hover:bg-violet-50 font-medium"
            onClick={() => { setOpen(false); onEditCanvas(); }}
          >
            <Edit className="w-3.5 h-3.5 text-violet-500" /> Edit Canvas
          </button>
          <button
            className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            onClick={() => { setOpen(false); onEdit(); }}
          >
            <Edit className="w-3.5 h-3.5 text-gray-400" /> Edit Details
          </button>
          <button
            className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            onClick={() => { setOpen(false); onPreview(); }}
          >
            <Eye className="w-3.5 h-3.5 text-gray-400" /> Preview
          </button>

          <div className="border-t border-gray-100 my-1" />

          {/* Workflow actions based on current status */}
          {status === 'draft' && (
            <button
              className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-amber-700 hover:bg-amber-50"
              onClick={() => { setOpen(false); onSubmit(); }}
            >
              <SendToBack className="w-3.5 h-3.5" /> Submit for Review
            </button>
          )}
          {status === 'pending' && (
            <>
              <button
                className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-blue-700 hover:bg-blue-50"
                onClick={() => { setOpen(false); onApprove(); }}
              >
                <CheckCircle className="w-3.5 h-3.5" /> Approve
              </button>
              <button
                className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                onClick={() => { setOpen(false); onReject(); }}
              >
                <ThumbsDown className="w-3.5 h-3.5" /> Reject
              </button>
            </>
          )}
          {status === 'approved' && (
            <button
              className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-emerald-700 hover:bg-emerald-50"
              onClick={() => { setOpen(false); onPublish(); }}
            >
              <Globe className="w-3.5 h-3.5" /> Publish
            </button>
          )}
          {status === 'published' && (
            <button
              className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              onClick={() => { setOpen(false); onUnpublish(); }}
            >
              <EyeOff className="w-3.5 h-3.5 text-gray-400" /> Unpublish
            </button>
          )}
          {status === 'rejected' && (
            <button
              className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-amber-700 hover:bg-amber-50"
              onClick={() => { setOpen(false); onSubmit(); }}
            >
              <SendToBack className="w-3.5 h-3.5" /> Resubmit
            </button>
          )}

          <div className="border-t border-gray-100 my-1" />
          <button
            className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50"
            onClick={() => { setOpen(false); onDelete(); }}
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Connection Error Banner ───────────────────────────────────────────────────
const ConnectionError = ({ url, onRetry }) => (
  <div className="rounded-xl border border-red-200 bg-red-50 px-5 py-4 flex items-start gap-3">
    <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
    <div className="flex-1">
      <p className="text-sm font-semibold text-red-800">Cannot reach the backend server</p>
      <p className="text-xs text-red-600 mt-0.5">
        Unable to connect to <code className="font-mono bg-red-100 px-1 rounded">{url || 'the API'}</code>.
        Make sure the backend is running (<code className="font-mono bg-red-100 px-1 rounded">uvicorn server:app --reload</code>)
        and that <code className="font-mono bg-red-100 px-1 rounded">REACT_APP_BACKEND_URL</code> is set correctly in the frontend <code className="font-mono bg-red-100 px-1 rounded">.env</code> file.
      </p>
    </div>
    <Button size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-100 gap-1.5 flex-shrink-0" onClick={onRetry}>
      <RefreshCw className="w-3.5 h-3.5" /> Retry
    </Button>
  </div>
);

const PAGE_SIZE = 50;

const STATUS_TABS = [
  { key: 'all',       label: 'All' },
  { key: 'draft',     label: 'Draft' },
  { key: 'pending',   label: 'Pending' },
  { key: 'approved',  label: 'Approved' },
  { key: 'published', label: 'Published' },
  { key: 'rejected',  label: 'Rejected' },
];

// ─── Templates List ────────────────────────────────────────────────────────────
const AdminTemplatesList = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectionError, setConnectionError] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  // Rejection dialog state
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [migrating, setMigrating] = useState(false);

  const handleMigrateImages = useCallback(async () => {
    setMigrating(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_URL}/api/admin/content/migrate-images`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      const data = await res.json();
      toast({
        title: 'Image migration complete',
        description: `${data.updated} template(s) updated · ${data.images_replaced} image(s) stored locally`,
      });
      fetchData();
    } catch (err) {
      toast({ title: 'Migration failed', description: err.message, variant: 'destructive' });
    } finally {
      setMigrating(false);
    }
  }, [toast]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch data (re-runs on tab / page change) ────────────────────────────────
  const fetchData = async (statusOverride, pageOverride) => {
    const activeStatus = statusOverride ?? filterStatus;
    const activePage   = pageOverride   ?? page;
    setLoading(true);
    setConnectionError(false);
    try {
      const token = localStorage.getItem('admin_token');
      const params = new URLSearchParams({ page: activePage, limit: PAGE_SIZE, exclude_ai: 'true' });
      if (activeStatus !== 'all') params.set('status', activeStatus);

      let tRes, cRes;
      try {
        [tRes, cRes] = await Promise.all([
          fetch(`${API_URL}/api/admin/content/templates?${params}`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_URL}/api/admin/content/categories?type=template`,   { headers: { Authorization: `Bearer ${token}` } }),
        ]);
      } catch {
        setConnectionError(true);
        return;
      }
      if (tRes.ok) {
        const data = await tRes.json();
        setTemplates(data);
        setHasMore(data.length === PAGE_SIZE);
      }
      if (cRes.ok) setCategories(await cRes.json());
      if (!tRes.ok && tRes.status !== 404) {
        toast({ title: `Error ${tRes.status}`, description: 'Failed to load templates', variant: 'destructive' });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Switch tab: reset page, re-fetch
  const handleTabChange = (key) => {
    setFilterStatus(key);
    setPage(1);
    setSearchQuery('');
    fetchData(key, 1);
  };

  // Pagination helpers
  const goToPage = (p) => {
    setPage(p);
    fetchData(undefined, p);
  };

  // ── CRUD / workflow ──────────────────────────────────────────────────────────
  const handleDelete = async (template) => {
    if (!window.confirm(`Delete "${template.name}"?`)) return;
    try {
      const token = localStorage.getItem('admin_token');
      await fetch(`${API_URL}/api/admin/content/templates/${template.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({ title: 'Deleted', description: 'Template removed' });
      fetchData();
    } catch {
      toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
    }
  };

  const workflowAction = async (templateId, action, body = {}) => {
    const token = localStorage.getItem('admin_token');
    const res = await fetch(`${API_URL}/api/admin/content/templates/${templateId}/${action}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  };

  const handleApprove = async (template) => {
    try {
      await workflowAction(template.id, 'approve');
      toast({ title: 'Approved', description: `"${template.name}" is approved` });
      fetchData();
    } catch {
      toast({ title: 'Error', description: 'Failed to approve', variant: 'destructive' });
    }
  };

  const handleRejectConfirm = async () => {
    if (!rejectTarget) return;
    try {
      await workflowAction(rejectTarget.id, 'reject', { rejection_reason: rejectReason || null });
      toast({ title: 'Rejected', description: `"${rejectTarget.name}" has been rejected` });
      setRejectTarget(null);
      setRejectReason('');
      fetchData();
    } catch {
      toast({ title: 'Error', description: 'Failed to reject', variant: 'destructive' });
    }
  };

  const handlePublish = async (template) => {
    try {
      await workflowAction(template.id, 'publish');
      toast({ title: 'Published', description: `"${template.name}" is now live` });
      fetchData();
    } catch {
      toast({ title: 'Error', description: 'Failed to publish', variant: 'destructive' });
    }
  };

  const handleUnpublish = async (template) => {
    try {
      await workflowAction(template.id, 'unpublish');
      toast({ title: 'Unpublished', description: `"${template.name}" moved back to approved` });
      fetchData();
    } catch {
      toast({ title: 'Error', description: 'Failed to unpublish', variant: 'destructive' });
    }
  };

  const handleSubmit = async (template) => {
    try {
      await workflowAction(template.id, 'submit');
      toast({ title: 'Submitted', description: `"${template.name}" sent for review` });
      fetchData();
    } catch {
      toast({ title: 'Error', description: 'Failed to submit', variant: 'destructive' });
    }
  };

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const getCategoryName = (id) => categories.find(c => c.id === id)?.name || '—';

  const formatDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return iso; }
  };

  // Client-side search + type filter (status & category are server-side)
  const filtered = templates.filter(t => {
    const q = searchQuery.toLowerCase();
    return (
      (!q || t.name?.toLowerCase().includes(q) || t.tags?.some(tag => tag.toLowerCase().includes(q))) &&
      (filterCategory === 'all' || t.category_id === filterCategory) &&
      (filterType === 'all' || t.template_type === filterType)
    );
  });

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <AdminLayout>
      <div className="space-y-5 w-full">

        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Templates</h1>
            <p className="text-gray-500 text-sm mt-0.5">Manage and publish design templates for users.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => fetchData()} className="gap-1.5">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleMigrateImages}
              disabled={migrating}
              className="gap-1.5 text-amber-700 border-amber-300 hover:bg-amber-50"
              title="Download all external Pixabay/Unsplash/Pexels images and store them locally"
            >
              {migrating
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <ImageIcon className="w-3.5 h-3.5" />}
              {migrating ? 'Migrating…' : 'Fix Images'}
            </Button>
            <Button
              className="bg-violet-600 hover:bg-violet-700 gap-1.5 flex-shrink-0"
              onClick={() => navigate('/admin/templates/new')}
            >
              <Plus className="w-4 h-4" /> Add Template
            </Button>
          </div>
        </div>

        {/* Connection error */}
        {connectionError && <ConnectionError url={API_URL} onRetry={fetchData} />}

        {/* Status Tabs */}
        <div className="flex items-center gap-1 border-b border-gray-200 overflow-x-auto">
          {STATUS_TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => handleTabChange(tab.key)}
              className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors
                ${filterStatus === tab.key
                  ? 'border-violet-600 text-violet-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Search + secondary filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[180px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search name or tag…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-9 bg-white border-gray-200"
            />
          </div>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-44 bg-white border-gray-200">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-44 bg-white border-gray-200">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {TEMPLATE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          {(filterCategory !== 'all' || filterType !== 'all' || searchQuery) && (
            <button
              className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1"
              onClick={() => { setFilterCategory('all'); setFilterType('all'); setSearchQuery(''); }}
            >
              <X className="w-3 h-3" /> Clear
            </button>
          )}
          <span className="text-xs text-gray-400 ml-auto">{filtered.length} result{filtered.length !== 1 ? 's' : ''}</span>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 tracking-wide w-16">Preview</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 tracking-wide">Template</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 tracking-wide">Category</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 tracking-wide">Tags</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 tracking-wide">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 tracking-wide">Created</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-20 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-12 h-12 bg-violet-100 rounded-2xl flex items-center justify-center">
                          <FileText className="w-6 h-6 text-violet-500" />
                        </div>
                        <p className="font-semibold text-gray-900">No templates found</p>
                        <p className="text-sm text-gray-500">
                          {searchQuery || filterCategory !== 'all' || filterType !== 'all'
                            ? 'Try clearing filters or switching tabs.'
                            : filterStatus !== 'all'
                              ? `No ${filterStatus} templates yet.`
                              : 'Generate or create your first template.'}
                        </p>
                        {filterStatus === 'all' && !searchQuery && filterCategory === 'all' && filterType === 'all' && (
                          <Button
                            className="bg-violet-600 hover:bg-violet-700 gap-1.5 mt-1"
                            onClick={() => navigate('/admin/generator')}
                          >
                            <Plus className="w-4 h-4" /> Generate templates
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : filtered.map(t => (
                  <tr key={t.id} className="hover:bg-gray-50/50 transition-colors">
                    {/* Preview thumbnail */}
                    <td className="px-4 py-3">
                      <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 border border-gray-200 flex-shrink-0">
                        <ThumbnailCell t={t} />
                      </div>
                    </td>

                    {/* Name + dimensions + rejection note */}
                    <td className="px-4 py-3 max-w-[220px]">
                      <p className="font-semibold text-gray-900 truncate">{t.name}</p>
                      <p className="text-xs text-gray-400 tabular-nums mt-0.5">
                        {t.canvas_width}×{t.canvas_height}px
                        {t.is_premium && (
                          <span className="ml-1.5 bg-amber-100 text-amber-700 text-[10px] font-semibold px-1.5 py-0.5 rounded-full">Pro</span>
                        )}
                        {t.source === 'ai_generated' && (
                          <span className="ml-1.5 bg-violet-100 text-violet-600 text-[10px] font-semibold px-1.5 py-0.5 rounded-full">AI</span>
                        )}
                      </p>
                      {t.status === 'rejected' && t.rejection_reason && (
                        <p className="text-[11px] text-red-500 mt-1 leading-tight truncate" title={t.rejection_reason}>
                          ✕ {t.rejection_reason}
                        </p>
                      )}
                    </td>

                    {/* Category */}
                    <td className="px-4 py-3 text-gray-600 text-sm">
                      {t.category
                        ? <span>{t.category}{t.subcategory ? <span className="text-gray-400"> / {t.subcategory}</span> : null}</span>
                        : getCategoryName(t.category_id)}
                    </td>

                    {/* Tags */}
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1 max-w-[160px]">
                        {(t.tags || []).slice(0, 3).map(tag => (
                          <span key={tag} className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded truncate">
                            {tag}
                          </span>
                        ))}
                        {(t.tags || []).length > 3 && (
                          <span className="text-[10px] text-gray-400">+{t.tags.length - 3}</span>
                        )}
                      </div>
                    </td>

                    {/* Status badge */}
                    <td className="px-4 py-3">
                      <Badge className={`text-[11px] border-0 ${STATUS_BADGE[t.status] || STATUS_BADGE.draft}`}>
                        {t.status || 'draft'}
                      </Badge>
                    </td>

                    {/* Created */}
                    <td className="px-4 py-3 text-gray-500 text-xs tabular-nums">{formatDate(t.created_at)}</td>

                    {/* Actions */}
                    <td className="px-4 py-3">
                      <div className="flex justify-end">
                        <ActionsMenu
                          template={t}
                          onEdit={() => navigate(`/admin/templates/${t.id}`)}
                          onEditCanvas={() => {
                            // Open the canvas editor with the full template in
                            // location state. EditorPage hydrates pages from
                            // template.template_json.pages — identical flow
                            // used by the designer portal (DesignerMyTemplates).
                            // Path is /admin/templates/{id}/edit so EditorPage
                            // can detect admin context and route saves back to
                            // the templates collection (see EditorPage admin
                            // save handler).
                            console.log('[AdminTemplates] Edit Canvas — navigating to editor', {
                              id: t.id,
                              name: t.name,
                              has_template_json: !!t.template_json,
                              pages_count: t.template_json?.pages?.length || 0,
                              canvas_width: t.canvas_width,
                              canvas_height: t.canvas_height,
                            });
                            navigate(`/admin/templates/${t.id}/edit`, { state: { template: t } });
                          }}
                          onPreview={() => setPreviewTemplate(t)}
                          onApprove={() => handleApprove(t)}
                          onReject={() => { setRejectTarget(t); setRejectReason(''); }}
                          onPublish={() => handlePublish(t)}
                          onUnpublish={() => handleUnpublish(t)}
                          onSubmit={() => handleSubmit(t)}
                          onDelete={() => handleDelete(t)}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination footer */}
            {(page > 1 || hasMore) && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50/50">
                <button
                  disabled={page === 1}
                  onClick={() => goToPage(page - 1)}
                  className="text-sm text-gray-600 hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  ← Previous
                </button>
                <span className="text-xs text-gray-400">Page {page}</span>
                <button
                  disabled={!hasMore}
                  onClick={() => goToPage(page + 1)}
                  className="text-sm text-gray-600 hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  Next →
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <PreviewDialog
        template={previewTemplate}
        open={!!previewTemplate}
        onClose={() => setPreviewTemplate(null)}
      />

      {/* Rejection reason dialog */}
      <Dialog open={!!rejectTarget} onOpenChange={() => { setRejectTarget(null); setRejectReason(''); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <ThumbsDown className="w-4 h-4" />
              Reject Template
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Rejecting <strong>"{rejectTarget?.name}"</strong>. Optionally provide a reason shown to the creator.
            </p>
            <textarea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              placeholder="Reason for rejection (optional)…"
              rows={3}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setRejectTarget(null); setRejectReason(''); }}>
                Cancel
              </Button>
              <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={handleRejectConfirm}>
                Confirm Reject
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

// ─── Template Form ─────────────────────────────────────────────────────────────
const AdminTemplateForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();
  const isNew = id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [templateTypes, setTemplateTypes] = useState([]);
  const [themes, setThemes] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [connectionError, setConnectionError] = useState(false);

  const [form, setForm] = useState({
    name: '',
    description: '',
    template_type_id: '',
    template_category_id: '',
    tags: '',
    canvas_width: 1080,
    canvas_height: 1080,
    thumbnail: '',
    preview_url: '',
    template_file_url: '',
    status: 'draft',
    is_premium: false,
    share_settings: {
      is_public: true,
      allow_use_template: true,
      allow_shared_sessions: true,
      allow_downloads: true,
    },
  });
  // Counter fields are read-only; kept separate from `form` so they
  // don't get included in PUT payloads (the backend ignores them anyway,
  // but the separation makes the data flow obvious).
  const [stats, setStats] = useState(null);

  const load = async () => {
    setLoading(true);
    setConnectionError(false);
    try {
      const token = localStorage.getItem('admin_token');

      // Fetch template types and themes in parallel (non-blocking)
      Promise.allSettled([
        fetch(`${API_URL}/api/public/template-types`).then(r => r.ok ? r.json() : []),
        fetch(`${API_URL}/api/admin/content/template-categories`, {
          headers: { Authorization: `Bearer ${token}` },
        }).then(r => r.ok ? r.json() : []),
      ]).then(([typesResult, themesResult]) => {
        if (typesResult.status === 'fulfilled') setTemplateTypes(Array.isArray(typesResult.value) ? typesResult.value : []);
        if (themesResult.status === 'fulfilled') setThemes(Array.isArray(themesResult.value) ? themesResult.value : []);
      });

      if (!isNew) {
        let tRes;
        try {
          tRes = await fetch(`${API_URL}/api/admin/content/templates/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
        } catch {
          setConnectionError(true);
          setLoading(false);
          return;
        }
        if (tRes.ok) {
          const data = await tRes.json();
          setForm({
            name: data.name || '',
            description: data.description || '',
            template_type_id: data.template_type_id || '',
            template_category_id: data.template_category_id || '',
            tags: (data.tags || []).join(', '),
            canvas_width: data.canvas_width || 1080,
            canvas_height: data.canvas_height || 1080,
            thumbnail: data.thumbnail || '',
            preview_url: data.preview_url || '',
            template_file_url: data.template_file_url || '',
            status: data.status || 'draft',
            is_premium: data.is_premium || false,
            share_settings: data.share_settings || {
              is_public: true,
              allow_use_template: true,
              allow_shared_sessions: true,
              allow_downloads: true,
            },
          });
          setStats({
            views: data.views || 0,
            uses: data.uses || 0,
            png_downloads: data.png_downloads || 0,
            jpg_downloads: data.jpg_downloads || 0,
            copies_created: data.copies_created || 0,
            pro_conversions: data.pro_conversions || 0,
          });
        }
      }
    } catch {
      setConnectionError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id, isNew]);

  const set = (key, value) => setForm(f => ({ ...f, [key]: value }));

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast({ title: 'Name required', variant: 'destructive' });
      return;
    }
    if (!form.template_category_id) {
      toast({ title: 'Theme required', description: 'Please select a theme for this template.', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const url = isNew
        ? `${API_URL}/api/admin/content/templates`
        : `${API_URL}/api/admin/content/templates/${id}`;
      const tags = form.tags.split(',').map(t => t.trim()).filter(Boolean);
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...form, tags }),
      });
      if (res.ok) {
        const saved = await res.json();
        toast({ title: 'Saved', description: `Template ${isNew ? 'created' : 'updated'}` });
        if (isNew) navigate(`/admin/templates/${saved.id}`, { replace: true });
      } else {
        const err = await res.json();
        toast({ title: 'Error', description: err.detail || 'Save failed', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Error', description: 'Network error', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6 w-full max-w-4xl">

        {/* Connection error */}
        {connectionError && (
          <ConnectionError url={API_URL} onRetry={load} />
        )}

        {/* Header */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/templates')} className="text-gray-500">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {isNew ? 'Add Template' : 'Edit Template'}
              </h1>
              <p className="text-gray-500 text-sm mt-0.5">
                {isNew ? 'Fill in template details and upload assets.' : `Editing: ${form.name}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {(form.thumbnail || form.preview_url) && (
              <Button variant="outline" size="sm" onClick={() => setPreviewOpen(true)} className="gap-1.5">
                <Eye className="w-4 h-4" /> Preview
              </Button>
            )}
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-violet-600 hover:bg-violet-700 gap-1.5"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? 'Saving…' : 'Save Template'}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-6">

          {/* Left — Main fields */}
          <div className="col-span-2 space-y-5">

            {/* Basic Info */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
              <h2 className="text-sm font-semibold text-gray-700">Basic Information</h2>

              <div className="space-y-2">
                <Label>Template Title *</Label>
                <Input
                  value={form.name}
                  onChange={e => set('name', e.target.value)}
                  placeholder="e.g., Modern Social Media Pack"
                />
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <textarea
                  value={form.description}
                  onChange={e => set('description', e.target.value)}
                  placeholder="Briefly describe what this template is for…"
                  rows={3}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Type (Format)</Label>
                  <Select value={form.template_type_id} onValueChange={v => set('template_type_id', v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="e.g. Flyer, Poster…" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">None</SelectItem>
                      {templateTypes.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>
                    Theme (Niche) <span className="text-red-500">*</span>
                  </Label>
                  <Select value={form.template_category_id} onValueChange={v => set('template_category_id', v)}>
                    <SelectTrigger className={!form.template_category_id ? 'border-red-200 focus:ring-red-500/20' : ''}>
                      <SelectValue placeholder="Select a theme…" />
                    </SelectTrigger>
                    <SelectContent>
                      {themes.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Tags</Label>
                <div className="flex items-center gap-2">
                  <Tag className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <Input
                    value={form.tags}
                    onChange={e => set('tags', e.target.value)}
                    placeholder="social, marketing, minimal (comma separated)"
                  />
                </div>
              </div>
            </div>

            {/* Canvas Dimensions */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
              <h2 className="text-sm font-semibold text-gray-700">Canvas Dimensions</h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Width (px)</Label>
                  <Input
                    type="number"
                    value={form.canvas_width}
                    onChange={e => set('canvas_width', parseInt(e.target.value) || 1080)}
                    min={100}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Height (px)</Label>
                  <Input
                    type="number"
                    value={form.canvas_height}
                    onChange={e => set('canvas_height', parseInt(e.target.value) || 1080)}
                    min={100}
                  />
                </div>
              </div>
              {/* Quick presets */}
              <div className="flex flex-wrap gap-2">
                {[
                  { label: 'Instagram Post', w: 1080, h: 1080 },
                  { label: 'Instagram Story', w: 1080, h: 1920 },
                  { label: 'YouTube Thumbnail', w: 1280, h: 720 },
                  { label: 'A4 Landscape', w: 3508, h: 2480 },
                  { label: 'Twitter Post', w: 1200, h: 675 },
                ].map(p => (
                  <button
                    key={p.label}
                    onClick={() => setForm(f => ({ ...f, canvas_width: p.w, canvas_height: p.h }))}
                    className={`text-xs px-2.5 py-1 rounded-lg border transition-colors ${
                      form.canvas_width === p.w && form.canvas_height === p.h
                        ? 'border-violet-300 bg-violet-50 text-violet-700 font-medium'
                        : 'border-gray-200 text-gray-500 hover:border-violet-200 hover:text-violet-600'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* File Uploads */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
              <h2 className="text-sm font-semibold text-gray-700">Assets & Files</h2>
              <UploadField
                label="Thumbnail Image"
                value={form.thumbnail}
                onChange={v => set('thumbnail', v)}
                hint="Shown in template listings — square or 4:3 recommended"
              />
              <UploadField
                label="Preview Image"
                value={form.preview_url}
                onChange={v => set('preview_url', v)}
                hint="Full preview shown in detail view — higher resolution"
              />
              <UploadField
                label="Template File (JSON)"
                value={form.template_file_url}
                onChange={v => set('template_file_url', v)}
                accept=".json,application/json"
                hint="Export from the designer portal as a .json file"
              />
            </div>
          </div>

          {/* Right — Sidebar options */}
          <div className="space-y-5">

            {/* Status */}
            <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-3">
              <h2 className="text-sm font-semibold text-gray-700">Status</h2>
              {['draft', 'published', 'archived'].map(s => (
                <label key={s} className="flex items-center gap-3 cursor-pointer group">
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors ${
                    form.status === s ? 'border-violet-600 bg-violet-600' : 'border-gray-300 group-hover:border-violet-300'
                  }`}>
                    {form.status === s && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800 capitalize">{s}</p>
                    <p className="text-xs text-gray-400">
                      {s === 'draft' ? 'Not visible to users' : s === 'published' ? 'Visible in template library' : 'Hidden, kept for records'}
                    </p>
                  </div>
                  <input type="radio" name="status" value={s} checked={form.status === s} onChange={() => set('status', s)} className="hidden" />
                </label>
              ))}
            </div>

            {/* Access */}
            <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-3">
              <h2 className="text-sm font-semibold text-gray-700">Access</h2>
              {[
                { value: false, label: 'Free', desc: 'Available to all users' },
                { value: true,  label: 'Premium', desc: 'Requires a paid plan' },
              ].map(opt => (
                <label key={opt.label} className="flex items-center gap-3 cursor-pointer group">
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors ${
                    form.is_premium === opt.value ? 'border-violet-600 bg-violet-600' : 'border-gray-300 group-hover:border-violet-300'
                  }`}>
                    {form.is_premium === opt.value && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">{opt.label}</p>
                    <p className="text-xs text-gray-400">{opt.desc}</p>
                  </div>
                  <input type="radio" name="access" checked={form.is_premium === opt.value} onChange={() => set('is_premium', opt.value)} className="hidden" />
                </label>
              ))}
            </div>

            {/* Sharing settings */}
            <SharingPanel
              value={form.share_settings || {}}
              onChange={(next) => set('share_settings', next)}
            />

            {/* Template stats (existing templates only) */}
            {!isNew && stats && <TemplateStatsCard template={stats} />}

            {/* Thumbnail preview card */}
            {form.thumbnail && (
              <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-3">
                <h2 className="text-sm font-semibold text-gray-700">Thumbnail Preview</h2>
                <img
                  src={form.thumbnail}
                  alt="Thumbnail"
                  className="w-full rounded-xl object-cover border border-gray-100"
                  style={{ maxHeight: 160 }}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Preview Dialog */}
      <PreviewDialog
        template={{ ...form, tags: form.tags.split(',').map(t => t.trim()).filter(Boolean) }}
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
      />
    </AdminLayout>
  );
};

// ─── Root ──────────────────────────────────────────────────────────────────────
const AdminTemplates = () => {
  const { id } = useParams();
  return id ? <AdminTemplateForm /> : <AdminTemplatesList />;
};

export default AdminTemplates;
